// Videos.tsx
import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete } from "react-icons/md";
import { BsYoutube } from "react-icons/bs";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import ButtonCreate from "../../components/drawer/ButtonCreate";
import ButtonEdit from "../../components/drawer/ButtonEdit";
import LinkEdit from "../../components/drawer/LinkEdit";
import {
  activeInactiveVideo,
  deleteVideo,
  getAllVideos,
} from "./services/video.service";
import { clearVideoID, setVideoID } from "./videoSlice";

const Videos: React.FC = () => {
  const dispatch = useAppDispatch();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    dispatch(getAllVideos());
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  return (
    <>
      <DeleteVideoDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0px 20px",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Video List
          </Typography.h4>
          {isAddAccess && (
            <ButtonCreate label="Add New Video" drawerName="videoForm" />
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <VideoGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const VideoGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const VideoList = useAppSelector((state) => state.video.VideoList);
  const loading = useAppSelector((state) => state.video.loading);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? VideoList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const handleWatchVideo = (videoUrl: string) => {
    window.open(videoUrl, "_blank");
  };

  const MyEditCommandCell = (props: any) => {
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteVideo"));
      dispatch(setVideoID(ID));
    };

    return (
      <td
        className={`action-td ${props.className}`}
        style={{ ...props.tdProps?.style }}
      >
        <IconButton
          type="button"
          fillMode="flat"
          size="small"
          title="Watch Video"
          className="color-primary"
          onClick={() => handleWatchVideo(props.dataItem.VideoURL)}
          icon={BsYoutube}
          iconStyle={{ fontSize: "20px", color: "#FF0000" }}
        />
        {isEditAccess && (
          <ButtonEdit
            ID={props.dataItem.ID}
            drawerName="videoForm"
            tooltipTitle="Edit"
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 6 || props.index === 7 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const MyActiveInActiveCommandCell = (props: any) => {
    const [IsActive, setIsActive] = useState<boolean>(
      props.dataItem.IsActive ?? false,
    );

    const handleActiveInActive = () => {
      setIsActive(!IsActive);
      const data = {
        ID: props.dataItem.ID,
        IsActive: !IsActive,
      };
      dispatch(activeInactiveVideo(data)).then((response: any) => {
        if (response.payload?.data?.Status === 200) {
          dispatch(getAllVideos());
        } else {
          dispatch(getAllVideos());
        }
      });
    };

    return (
      <td
        style={{ textAlign: "center", ...props.tdProps?.style }}
        className={props.tdProps?.className}
      >
        <Switch
          disabled={isEditAccess ? false : true}
          name="IsActive"
          checked={IsActive}
          onChange={handleActiveInActive}
          size="small"
        />
      </td>
    );
  };

  const DisplayHomeCell = (props: GridCellProps) => (
    <td style={{ ...props.style }} className={props.className}>
      <span
        style={{
          padding: "3px 8px",
          borderRadius: 4,
          backgroundColor: props.dataItem?.IsDisplayHome
            ? "#d4edda"
            : "#f8d7da",
          color: props.dataItem?.IsDisplayHome ? "#155724" : "#721c24",
          fontSize: 12,
          fontWeight: 500,
        }}
      >
        {props.dataItem?.IsDisplayHome ? "YES" : "NO"}
      </span>
    </td>
  );
  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      {VideoList && VideoList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          filterable={true}
          filter={filter}
          className="responsive-table"
          data={
            filter
              ? filterBy(VideoList, filter).slice(
                  page.skip,
                  page.take + page.skip,
                )
              : VideoList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={VideoList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [10, 20, 50, 100],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="Title"
            title="Video Title"
            width={250}
            cells={{
              data: (props: any) => (
                <td
                  className={`${isEditAccess ? "hoverableName" : "normalName"}`}
                >
                  {isEditAccess ? (
                    <LinkEdit
                      ID={props.dataItem.ID}
                      drawerName="videoForm"
                      label={props.dataItem?.Title}
                    />
                  ) : (
                    props.dataItem?.Title
                  )}
                </td>
              ),
            }}
          />

          <Column
            field="Tagline"
            title="Tagline"
            width={200}
            cells={{
              data: (props: any) => (
                <td>
                  <div
                    id={`tagline-${props.dataItem.ID}`}
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "180px",
                    }}
                  >
                    {props.dataItem?.Tagline || "-"}
                  </div>
                </td>
              ),
            }}
          />

          <Column
            field="ShortDescription"
            title="Description"
            width={250}
            cells={{
              data: (props: any) => (
                <td>
                  <div
                    id={`desc-${props.dataItem.ID}`}
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "230px",
                    }}
                  >
                    {props.dataItem?.ShortDescription || "-"}
                  </div>
                </td>
              ),
            }}
          />

          <Column
            field="Date"
            title="Date"
            width={120}
            cells={{
              data: (props: any) => <td>{props.dataItem?.Date}</td>,
            }}
          />

          <Column
            field="SequenceNo"
            title="Sequence"
            width={90}
            cells={{
              data: (props: any) => (
                <td style={{ textAlign: "center" }}>
                  {props.dataItem?.SequenceNo || 0}
                </td>
              ),
            }}
          />

          <Column
            field="IsDisplayHome"
            title="Display Home"
            width={110}
            cells={{ data: DisplayHomeCell }}
            filterable={false}
          />

          <Column
            field="ID"
            title="Status"
            cells={{ data: MyActiveInActiveCommandCell }}
            width={"90px"}
            filterable={false}
            locked={true}
          />

          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              cells={{ data: MyEditCommandCell }}
              width={"140px"}
              locked={true}
              filterable={false}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteVideoDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.video.loading);
  const VideoID = useAppSelector((state) => state.video.VideoID);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteVideo(ID));
      if (response.payload.Data.Status === 200) {
        dispatch(getAllVideos());
        dispatch(closeDialog());
        dispatch(clearVideoID());
      } else {
        dispatch(getAllVideos());
        dispatch(closeDialog());
        dispatch(clearVideoID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteVideo" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Video"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Video?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(VideoID)}
                  type="button"
                  disabled={loading}
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default Videos;
